import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { accountingService } from '../../services/accountingService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { DocumentArrowDownIcon, PrinterIcon } from '@heroicons/react/24/outline';

const FinancialReports = () => {
  const [reportType, setReportType] = useState('trial-balance');
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateRange, setDateRange] = useState({
    from_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0]
  });
  const [selectedAccount, setSelectedAccount] = useState('');

  const { data: trialBalance, isLoading: tbLoading } = useQuery(
    ['trial-balance', asOnDate],
    () => accountingService.getTrialBalance(asOnDate),
    { enabled: reportType === 'trial-balance' }
  );

  const { data: profitLoss, isLoading: plLoading } = useQuery(
    ['profit-loss', dateRange],
    () => accountingService.getProfitLoss(dateRange.from_date, dateRange.to_date),
    { enabled: reportType === 'profit-loss' }
  );

  const { data: balanceSheet, isLoading: bsLoading } = useQuery(
    ['balance-sheet', asOnDate],
    () => accountingService.getBalanceSheet(asOnDate),
    { enabled: reportType === 'balance-sheet' }
  );

  const { data: cashFlow, isLoading: cfLoading } = useQuery(
    ['cash-flow', dateRange],
    () => accountingService.getCashFlow(dateRange.from_date, dateRange.to_date),
    { enabled: reportType === 'cash-flow' }
  );

  const { data: generalLedger, isLoading: glLoading } = useQuery(
    ['general-ledger', selectedAccount, dateRange],
    () => accountingService.getGeneralLedger(selectedAccount, dateRange.from_date, dateRange.to_date),
    { enabled: reportType === 'general-ledger' && selectedAccount }
  );

  const { data: accountsData } = useQuery(
    'chart-of-accounts-list',
    () => accountingService.getChartOfAccounts(),
    { enabled: reportType === 'general-ledger' }
  );

  const isLoading = tbLoading || plLoading || bsLoading || cfLoading || glLoading;

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    // Export to Excel functionality
    alert('Export feature coming soon');
  };

  const reports = [
    { id: 'trial-balance', name: 'Trial Balance' },
    { id: 'profit-loss', name: 'Profit & Loss' },
    { id: 'balance-sheet', name: 'Balance Sheet' },
    { id: 'cash-flow', name: 'Cash Flow' },
    { id: 'general-ledger', name: 'General Ledger' }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold mb-2">Financial Reports</h2>
          <p className="text-gray-600">View and export financial statements</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Export
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="flex space-x-2 border-b mb-6">
        {reports.map((report) => (
          <button
            key={report.id}
            onClick={() => setReportType(report.id)}
            className={`px-4 py-2 capitalize ${
              reportType === report.id
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {report.name}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(reportType === 'trial-balance' || reportType === 'balance-sheet') && (
            <div>
              <label className="block text-sm font-medium mb-1">As of Date</label>
              <input
                type="date"
                value={asOnDate}
                onChange={(e) => setAsOnDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          )}

          {(reportType === 'profit-loss' || reportType === 'cash-flow') && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <input
                  type="date"
                  value={dateRange.from_date}
                  onChange={(e) => setDateRange({ ...dateRange, from_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Date</label>
                <input
                  type="date"
                  value={dateRange.to_date}
                  onChange={(e) => setDateRange({ ...dateRange, to_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </>
          )}

          {reportType === 'general-ledger' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Account</label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Account</option>
                  {accountsData?.data?.accounts?.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_code} - {account.account_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <input
                  type="date"
                  value={dateRange.from_date}
                  onChange={(e) => setDateRange({ ...dateRange, from_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Date</label>
                <input
                  type="date"
                  value={dateRange.to_date}
                  onChange={(e) => setDateRange({ ...dateRange, to_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Report Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Trial Balance */}
          {reportType === 'trial-balance' && trialBalance?.data && (
            <div>
              <h3 className="text-xl font-bold text-center mb-2">Trial Balance</h3>
              <p className="text-center text-gray-500 mb-6">As of {formatDate(asOnDate)}</p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trialBalance.data.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.account_name}</td>
                        <td className="px-6 py-4 text-sm text-right">{formatCurrency(item.debit_balance)}</td>
                        <td className="px-6 py-4 text-sm text-right">{formatCurrency(item.credit_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td className="px-6 py-4 text-right">Total:</td>
                      <td className="px-6 py-4 text-right">{formatCurrency(trialBalance.summary.total_debit)}</td>
                      <td className="px-6 py-4 text-right">{formatCurrency(trialBalance.summary.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {trialBalance.summary.is_balanced && (
                <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-center">
                  ✓ Trial balance is balanced
                </div>
              )}
            </div>
          )}

          {/* Profit & Loss */}
          {reportType === 'profit-loss' && profitLoss?.data && (
            <div>
              <h3 className="text-xl font-bold text-center mb-2">Profit & Loss Statement</h3>
              <p className="text-center text-gray-500 mb-6">
                For the period {formatDate(dateRange.from_date)} to {formatDate(dateRange.to_date)}
              </p>

              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3">Income</h4>
                <table className="min-w-full">
                  <tbody>
                    {profitLoss.data.income.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-2 text-gray-900">{item.account_name}</td>
                        <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-t font-bold">
                      <td className="py-2">Total Income</td>
                      <td className="py-2 text-right">{formatCurrency(profitLoss.data.total_income)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3">Expenses</h4>
                <table className="min-w-full">
                  <tbody>
                    {profitLoss.data.expenses.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-2 text-gray-900">{item.account_name}</td>
                        <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    <tr className="border-t font-bold">
                      <td className="py-2">Total Expenses</td>
                      <td className="py-2 text-right">{formatCurrency(profitLoss.data.total_expense)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between font-bold text-lg">
                  <span>Net Profit / (Loss)</span>
                  <span className={profitLoss.data.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatCurrency(profitLoss.data.net_profit)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Balance Sheet */}
          {reportType === 'balance-sheet' && balanceSheet?.data && (
            <div>
              <h3 className="text-xl font-bold text-center mb-2">Balance Sheet</h3>
              <p className="text-center text-gray-500 mb-6">As of {formatDate(asOnDate)}</p>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-3">Assets</h4>
                  <table className="min-w-full">
                    <tbody>
                      {balanceSheet.data.assets.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 text-gray-900">{item.account_name}</td>
                          <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                      <tr className="border-t font-bold">
                        <td className="py-2">Total Assets</td>
                        <td className="py-2 text-right">{formatCurrency(balanceSheet.data.total_assets)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-3">Liabilities & Equity</h4>
                  <h5 className="font-medium mt-2">Liabilities</h5>
                  <table className="min-w-full">
                    <tbody>
                      {balanceSheet.data.liabilities.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 text-gray-900">{item.account_name}</td>
                          <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                      <tr className="border-b">
                        <td className="py-2">Total Liabilities</td>
                        <td className="py-2 text-right">{formatCurrency(balanceSheet.data.total_liabilities)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <h5 className="font-medium mt-4">Equity</h5>
                  <table className="min-w-full">
                    <tbody>
                      {balanceSheet.data.equity.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 text-gray-900">{item.account_name}</td>
                          <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                      <tr className="border-t font-bold">
                        <td className="py-2">Total Equity</td>
                        <td className="py-2 text-right">{formatCurrency(balanceSheet.data.total_equity)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between font-bold">
                      <span>Total Liabilities & Equity</span>
                      <span>{formatCurrency(balanceSheet.data.total_liabilities + balanceSheet.data.total_equity)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {balanceSheet.data.is_balanced && (
                <div className="mt-6 p-3 bg-green-50 text-green-700 rounded-lg text-center">
                  ✓ Balance sheet is balanced
                </div>
              )}
            </div>
          )}

          {/* Cash Flow */}
          {reportType === 'cash-flow' && cashFlow?.data && (
            <div>
              <h3 className="text-xl font-bold text-center mb-2">Cash Flow Statement</h3>
              <p className="text-center text-gray-500 mb-6">
                For the period {formatDate(dateRange.from_date)} to {formatDate(dateRange.to_date)}
              </p>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold mb-3">Operating Activities</h4>
                  <table className="min-w-full">
                    <tbody>
                      {cashFlow.data.operating_activities.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 capitalize">{item.activity?.replace('_', ' ')}</td>
                          <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-3">Investing Activities</h4>
                  <table className="min-w-full">
                    <tbody>
                      {cashFlow.data.investing_activities.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 capitalize">{item.activity?.replace('_', ' ')}</td>
                          <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-3">Financing Activities</h4>
                  <table className="min-w-full">
                    <tbody>
                      {cashFlow.data.financing_activities.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 capitalize">{item.activity?.replace('_', ' ')}</td>
                          <td className="py-2 text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Net Cash Flow</span>
                    <span className={cashFlow.data.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(cashFlow.data.net_cash_flow)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* General Ledger */}
          {reportType === 'general-ledger' && generalLedger?.data && (
            <div>
              <h3 className="text-xl font-bold text-center mb-2">General Ledger</h3>
              <p className="text-center text-gray-500 mb-2">
                Account: {generalLedger.data.account.account_name} ({generalLedger.data.account.account_code})
              </p>
              <p className="text-center text-gray-500 mb-6">
                Period: {formatDate(dateRange.from_date)} to {formatDate(dateRange.to_date)}
              </p>

              <div className="mb-4 flex justify-between">
                <div>Opening Balance: <strong>{formatCurrency(generalLedger.data.opening_balance)}</strong></div>
                <div>Closing Balance: <strong>{formatCurrency(generalLedger.data.closing_balance)}</strong></div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {generalLedger.data.transactions.map((txn, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(txn.entry_date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">{txn.voucher_no}</td>
                        <td className="px-6 py-4 text-sm">{txn.description}</td>
                        <td className="px-6 py-4 text-sm text-right">{formatCurrency(txn.debit)}</td>
                        <td className="px-6 py-4 text-sm text-right">{formatCurrency(txn.credit)}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium">{formatCurrency(txn.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinancialReports;